import React, { useEffect, useState } from 'react';
import { backupAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Download, Upload, Trash2, HardDrive, Archive, Clock, FileArchive, AlertTriangle, Sparkles } from 'lucide-react';

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const BackupAdmin = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => { loadBackups(); }, []);

  const loadBackups = async () => {
    try {
      const res = await backupAPI.list();
      setBackups(res.data);
    } catch (error) {
      toast.error('Nepodarilo se nacíst zálohy');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await backupAPI.create();
      toast.success(res.data.message || 'Záloha vytvořena');
      loadBackups();
    } catch (error) {
      toast.error('Nepodarilo se vytvořit zálohu');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (filename) => {
    const token = localStorage.getItem('token');
    const url = `${backupAPI.download(filename)}?token=${encodeURIComponent(token)}`;
    // Přímý browser download — spolehlivý i pro velké soubory (streamuje na disk)
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!window.confirm('Opravdu chcete obnovit zálohu? Současná data budou prepásána!')) {
      e.target.value = '';
      return;
    }

    setRestoring(true);
    try {
      const res = await backupAPI.restore(file);
      toast.success(res.data.message || 'Záloha obnovena');
      loadBackups();
    } catch (error) {
      toast.error('Nepodarilo se obnovit zálohu');
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm('Opravdu chcete smazat tuto zálohu?')) return;
    try {
      await backupAPI.delete(filename);
      toast.success('Záloha smazána');
      loadBackups();
    } catch (error) {
      toast.error('Nepodarilo se smazat zálohu');
    }
  };

  const handleMigrate = async () => {
    try {
      setMigrating(true);
      const res = await backupAPI.migrateToGridFS();
      const { migrated, already_in_gridfs, failed } = res.data;
      if (migrated === 0 && already_in_gridfs === 0) {
        toast.info('Žádné soubory k migraci.');
      } else if (failed > 0) {
        toast.warning(`Migrováno ${migrated} souborů. ${already_in_gridfs} už bylo trvale uloženo. ${failed} se nepodařilo.`);
      } else {
        toast.success(`Hotovo. Trvale uloženo ${migrated} nových souborů. ${already_in_gridfs} už bylo bezpečné.`);
      }
    } catch (e) {
      toast.error('Nepodařilo se spustit migraci. Backend možná nepodporuje tuto funkci — redeployujte.');
    } finally {
      setMigrating(false);
    }
  };

  const handleCleanup = async () => {
    try {
      setCleaning(true);
      const dry = await backupAPI.cleanupOrphaned(true);
      const count = dry.data.orphaned_count;
      const sizeMb = (dry.data.total_freed_bytes / (1024 * 1024)).toFixed(2);
      if (count === 0) {
        toast.success('Žádné nepoužívané soubory — vše je čisté.');
        return;
      }
      if (!window.confirm(`Najdeno ${count} nepoužívaných souborů (${sizeMb} MB). Opravdu je smazat?`)) return;
      const res = await backupAPI.cleanupOrphaned(false);
      toast.success(`Smazáno ${res.data.deleted_count} souborů (${(res.data.total_freed_bytes / (1024 * 1024)).toFixed(2)} MB uvolněno)`);
    } catch (error) {
      toast.error('Nepodařilo se vyčistit soubory');
    } finally {
      setCleaning(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">Zálohy</h1>
        <p className="text-[#64748B]">Správa záloh databáze a nahraných souboru</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
        <Button
          onClick={handleCreate}
          disabled={creating}
          className="h-auto py-6 rounded-2xl bg-[#4ECDC4] hover:bg-[#3DBDB5] flex flex-col items-center gap-2"
          data-testid="create-backup-btn"
        >
          <Archive size={28} />
          <span className="text-base font-semibold">{creating ? 'Vytvárím zálohu...' : 'Vytvořit zálohu'}</span>
          <span className="text-xs opacity-80">Export databáze a souboru</span>
        </Button>

        <label className={`cursor-pointer ${restoring ? 'pointer-events-none opacity-50' : ''}`}>
          <input type="file" accept=".zip" className="hidden" onChange={handleRestore} />
          <div className="h-full py-6 rounded-2xl border-2 border-dashed border-slate-300 hover:border-[#4ECDC4] transition-colors flex flex-col items-center justify-center gap-2 text-[#64748B] hover:text-[#4ECDC4]" data-testid="restore-backup-area">
            <Upload size={28} />
            <span className="text-base font-semibold">{restoring ? 'Obnovuji...' : 'Nahrát a obnovit zálohu'}</span>
            <span className="text-xs">ZIP soubor se zálohou</span>
          </div>
        </label>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-2xl">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <strong>Upozornení:</strong> Obnova zálohy prepíše všechna aktuální data v databázi (krome uživatelských úctu). 
            Pred obnovou doporucujeme nejprve vytvořit aktuální zálohu.
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 max-w-2xl flex items-center gap-4 flex-wrap">
        <div className="flex items-start gap-2 flex-1 min-w-[200px]">
          <Sparkles size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-emerald-800">
            <strong>Úklid disku:</strong> Smaže nahrané soubory, které již nejsou nikde použity (smazané fotky, dokumenty atd.). Záloha pak bude menší.
          </div>
        </div>
        <Button
          onClick={handleCleanup}
          disabled={cleaning}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700"
          data-testid="cleanup-orphaned-btn"
        >
          <Sparkles size={16} className="mr-2" />
          {cleaning ? 'Pracuji...' : 'Vyčistit nepoužívané soubory'}
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 max-w-2xl flex items-center gap-4 flex-wrap">
        <div className="flex items-start gap-2 flex-1 min-w-[200px]">
          <HardDrive size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <strong>Persistentní úložiště:</strong> Přesune všechny stávající nahrané soubory do MongoDB (databáze), aby přežily redeploy webu. Stačí spustit jednou po nahrání aktualizace.
          </div>
        </div>
        <Button
          onClick={handleMigrate}
          disabled={migrating}
          className="rounded-full bg-blue-600 hover:bg-blue-700"
          data-testid="migrate-gridfs-btn"
        >
          <HardDrive size={16} className="mr-2" />
          {migrating ? 'Migruji...' : 'Zajistit trvalé úložiště'}
        </Button>
      </div>

      <div className="max-w-2xl">
        <h2 className="text-lg font-semibold text-[#2C3E50] mb-4 flex items-center gap-2">
          <HardDrive size={20} />
          Uložené zálohy ({backups.length})
        </h2>

        {backups.length > 0 ? (
          <div className="space-y-3">
            {backups.map((backup) => (
              <div key={backup.filename} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4" data-testid={`backup-${backup.filename}`}>
                <div className="w-12 h-12 rounded-xl bg-[#4ECDC4]/10 flex items-center justify-center flex-shrink-0">
                  <FileArchive size={24} className="text-[#4ECDC4]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#2D3748] text-sm truncate">{backup.filename}</div>
                  <div className="flex items-center gap-3 text-xs text-[#64748B] mt-1">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(backup.created_at).toLocaleString('cs-CZ')}
                    </span>
                    <span>{formatSize(backup.size)}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleDownload(backup.filename)} data-testid={`download-${backup.filename}`}>
                    <Download size={14} className="mr-1" /> Stáhnout
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(backup.filename)} data-testid={`delete-${backup.filename}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
            <Archive size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-[#64748B]">Žádné zálohy</p>
            <p className="text-sm text-[#64748B] mt-1">Vytvořte první zálohu kliknutím na tlacítko výše</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupAdmin;
